import React, { useEffect, useState } from 'react';
import { User, Phone, IdCard, Upload, X } from 'lucide-react';

/** Step 2 of 5 — Identity. Captures business/personal name, phone, SA ID number,
 *  and the two files (ID document + selfie) needed for manual admin verification. */
const StepIdentity = ({ data, onChange }) => {
  const [idPreview, setIdPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);

  useEffect(() => {
    if (!data.idDocumentFile) { setIdPreview(null); return; }
    const url = URL.createObjectURL(data.idDocumentFile);
    setIdPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [data.idDocumentFile]);

  useEffect(() => {
    if (!data.selfieFile) { setSelfiePreview(null); return; }
    const url = URL.createObjectURL(data.selfieFile);
    setSelfiePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [data.selfieFile]);

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <User size={16} /> Full Name / Business Name *
        </label>
        <input
          type="text" data-testid="identity-name-input" value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="John's Plumbing Services"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Phone size={16} /> Phone Number *
        </label>
        <input
          type="tel" data-testid="identity-phone-input" value={data.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          placeholder="+27 82 123 4567"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <IdCard size={16} /> SA ID Number / Passport Number *
        </label>
        <input
          type="text" data-testid="identity-id-number-input" value={data.idNumber}
          onChange={(e) => onChange({ idNumber: e.target.value })}
          placeholder="8001015009087"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* ID Document */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">ID Document Photo *</label>
          {idPreview ? (
            <div className="relative">
              <img src={idPreview} alt="ID preview" data-testid="id-document-preview" className="w-full h-36 object-cover rounded-lg border border-gray-200" />
              <button
                type="button" onClick={() => onChange({ idDocumentFile: null })}
                data-testid="id-document-remove-btn"
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-sm"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-gray-300 hover:border-[#37b34a] rounded-lg cursor-pointer text-gray-500 hover:text-[#37b34a] transition-colors">
              <Upload size={20} />
              <span className="text-xs mt-2">Upload ID photo</span>
              <input
                type="file" accept="image/*,application/pdf" className="hidden"
                data-testid="id-document-file-input"
                onChange={(e) => onChange({ idDocumentFile: e.target.files?.[0] || null })}
              />
            </label>
          )}
        </div>

        {/* Selfie */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Selfie Photo *</label>
          {selfiePreview ? (
            <div className="relative">
              <img src={selfiePreview} alt="Selfie preview" data-testid="selfie-preview" className="w-full h-36 object-cover rounded-lg border border-gray-200" />
              <button
                type="button" onClick={() => onChange({ selfieFile: null })}
                data-testid="selfie-remove-btn"
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-sm"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-gray-300 hover:border-[#37b34a] rounded-lg cursor-pointer text-gray-500 hover:text-[#37b34a] transition-colors">
              <Upload size={20} />
              <span className="text-xs mt-2">Upload a selfie</span>
              <input
                type="file" accept="image/*" capture="user" className="hidden"
                data-testid="selfie-file-input"
                onChange={(e) => onChange({ selfieFile: e.target.files?.[0] || null })}
              />
            </label>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
        Your ID document and selfie are reviewed manually by our team (usually within 24-48 hours).
        You can finish setting up your profile while verification is in progress, but you won't be able
        to unlock leads until it's approved.
      </p>
    </div>
  );
};

export default StepIdentity;
